import type { ComponentProps, ReactNode } from 'react'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { HintedLabel } from '@/src/components/FieldHint'

interface FieldProps {
  id: string
  label: ReactNode
  value: string
  onChange: (value: string) => void
  error?: string
  description?: ReactNode
  /** "What is this for?", behind an info icon beside the label. */
  hint?: ReactNode
  required?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

function LabelWithHint({
  id,
  label,
  hint,
  required,
}: Pick<FieldProps, 'id' | 'label' | 'hint' | 'required'>) {
  return hint ? (
    <HintedLabel htmlFor={id} hint={hint} required={required}>
      {label}
    </HintedLabel>
  ) : (
    <FieldLabel htmlFor={id} required={required}>
      {label}
    </FieldLabel>
  )
}

/** Ties a control to its label, help text and error the way screen readers expect. */
function describedBy(id: string, error?: string, description?: ReactNode) {
  return (
    [error ? `${id}-error` : null, description ? `${id}-description` : null]
      .filter(Boolean)
      .join(' ') || undefined
  )
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  description,
  hint,
  required,
  disabled,
  placeholder,
  className,
  ...inputProps
}: FieldProps & Omit<ComponentProps<typeof Input>, keyof FieldProps | 'id'>) {
  return (
    <Field data-invalid={Boolean(error)} className={className}>
      <LabelWithHint id={id} label={label} hint={hint} required={required} />
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy(id, error, description)}
        {...inputProps}
      />
      {description && <FieldDescription id={`${id}-description`}>{description}</FieldDescription>}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </Field>
  )
}

export function TextAreaField({
  id,
  label,
  value,
  onChange,
  error,
  description,
  hint,
  required,
  disabled,
  placeholder,
  className,
  rows = 3,
}: FieldProps & { rows?: number }) {
  return (
    <Field data-invalid={Boolean(error)} className={className}>
      <LabelWithHint id={id} label={label} hint={hint} required={required} />
      <Textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy(id, error, description)}
      />
      {description && <FieldDescription id={`${id}-description`}>{description}</FieldDescription>}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </Field>
  )
}

const LABELS: Record<string, string> = {
  WHATSAPP: 'WhatsApp',
  WALK_IN: 'Walk-in',
  PVOC_TESTING: 'PVoC testing',
  END_TO_END_IMPORT: 'End-to-end import',
  BOTH: 'Email and WhatsApp',
}

/** An enum value as people read it: "CUSTOMS_CLEARANCE" is "Customs clearance". */
export function formatLabel(value: string) {
  if (LABELS[value]) return LABELS[value]
  const words = value.toLowerCase().replaceAll('_', ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
export const isLink = (value: string) => /^https?:\/\/\S+\.\S+/i.test(value.trim())
